import { Observable, of, switchMap } from 'rxjs';
import { Injectable } from '@angular/core';
import { updateDoc } from 'firebase/firestore'
import { collectionData, collectionGroup, doc, docData, Firestore, orderBy, query, where } from '@angular/fire/firestore';

import { VolumeWithId, BB, VolumeTag } from '@innersight-os/shared/firestore-interfaces';
import { arrayQuery } from '../utils';
import { logAndRetry } from '@innersight-os/shared/util-rxjs';
import { UsersService } from '../users/users.service';
import { DocumentData } from '@firebase/firestore-types';


@Injectable({
  providedIn: 'root'
})
export class VolumesService {
  constructor(
    private db: Firestore,
    private users: UsersService,
  ) {}

  getVolumesForOperation = (operationId: string): Observable<VolumeWithId[]> =>
    collectionData(
      query<VolumeWithId, DocumentData>(
        // eslint-able-next-line @typescript-eslint/no-explicit-any
        collectionGroup(this.db, 'volumes') as any,
        where('operationId', '==', operationId),
        orderBy('acquisitionDate', 'desc')
      ),
      {idField: 'id'},
    ) as Observable<VolumeWithId[]>

  getVolume = (operationId: string, uploadId: string, id: string): Observable<VolumeWithId> =>
    docData<VolumeWithId>(
      // eslint-able-next-line @typescript-eslint/no-explicit-any
      doc(this.db, `operations/${operationId}/uploads/${uploadId}/volumes/${id}`) as any,
      {idField: 'id'},
    ).pipe(
      logAndRetry(`operations/${operationId}/uploads/${uploadId}/volumes/${id}`),
    )

  getDuplicateVolumes = (sha256s: string[]): Observable<VolumeWithId[]> =>
    this.users.iAmAnAdmin$.pipe(
      switchMap(iAmAnAdmin => iAmAnAdmin
        ? arrayQuery(
            query<VolumeWithId, DocumentData>(
              // eslint-able-next-line @typescript-eslint/no-explicit-any
              collectionGroup(this.db, 'volumes') as any,
            ),
            'sha256',
            'in',
            sha256s.filter(Boolean),
          )
        : of([]) // only available to admins right now
      )
    )

  setBoundingBox(operationId: string, uploadId: string, volumeId: string, boundingBox: BB) {
    return updateDoc(this.doc(operationId, uploadId, volumeId), {boundingBox})
  }

  setContrastWindow(operationId: string, uploadId: string, volumeId: string, contrastWindow: [number, number]) {
    return updateDoc(this.doc(operationId, uploadId, volumeId), {contrastWindow})
  }

  setVolumeTags(operationId: string, uploadId: string, volumeId: string, tags: VolumeTag[]) {
    return updateDoc(this.doc(operationId, uploadId, volumeId), {tags})
  }

  setHidden(operationId: string, uploadId: string, volumeId: string, hidden: boolean) {
    return updateDoc(this.doc(operationId, uploadId, volumeId), {hidden: !hidden})
  }

  private doc(operationId: string, uploadId: string, volumeId: string) {
    return doc(this.db, `operations/${operationId}/uploads/${uploadId}/volumes/${volumeId}`)
  }
}
